import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useBondIssuers, useCreateBondIssuer } from "@/hooks/useBondIssuers";
import { CreatableSelect } from "@/components/creatable-select/CreatableSelect";
import {
  debtSecurityFormSchema,
  DebtSecurityFormData,
} from "./debtSecurityForm.schema";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import {DebtSecurity} from "@/integrations/supabase/debt.models.ts";

interface DebtSecurityFormProps {
  debtSecurity?: DebtSecurity;
  onSave: (data: DebtSecurityFormData) => void | Promise<void>;
  onCancel: () => void;
}

export function DebtSecurityForm({
  debtSecurity,
  onSave,
  onCancel,
}: DebtSecurityFormProps) {
  const { toast } = useToast();
  const { data: issuers = [], isLoading: isLoadingIssuers } = useBondIssuers();
  const createIssuerMutation = useCreateBondIssuer();
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DebtSecurityFormData>({
    resolver: zodResolver(debtSecurityFormSchema),
    defaultValues: {
      isin: debtSecurity?.isin ?? "",
      issuerId: debtSecurity?.issuer_id ?? "",
      couponRate: debtSecurity?.coupon_rate ?? 0,
      nominalPrice: debtSecurity?.nominal_price ?? 0,
      quantity: debtSecurity?.quantity ?? 0,
      tenor: debtSecurity?.tenor ?? "",
      maturityDate: debtSecurity?.maturity_date ?? "",
      currency: debtSecurity?.currency ?? "USD",
      issueNumber: debtSecurity?.issue_number ?? "",
      notes: debtSecurity?.notes ?? "",
      issueDate: debtSecurity?.issue_date ?? null,
    },
  });

  const quantity = watch("quantity");
  const nominalPrice = watch("nominalPrice");
  const issuerId = watch("issuerId");

  // Calculate face value
  const faceValue = quantity && nominalPrice ? quantity * nominalPrice : 0;

  const onSubmit = async (data: DebtSecurityFormData) => {
    try {
      await onSave(data);
      toast({
        title: "Success",
        description: `Debt security ${debtSecurity ? "updated" : "created"} successfully`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${debtSecurity ? "update" : "create"} debt security. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const handleCreateIssuer = async (name: string): Promise<string> => {
    try {
      const newIssuer = await createIssuerMutation.mutateAsync({
        name,
        country: null,
        issuer_type: null,
      });
      return newIssuer.id;
    } catch (error) {
      throw new Error("Failed to create issuer");
    }
  };

  const issuerOptions = issuers.map((issuer) => ({
    value: issuer.id,
    label: issuer.name,
  }));

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      aria-label={debtSecurity ? "Edit debt security" : "Add debt security"}>
      <div className="space-y-2">
        <Label htmlFor="isin">ISIN *</Label>
        <Input
          id="isin"
          {...register("isin")}
          placeholder="e.g. US0378331005"
          maxLength={12}
          className="uppercase"
          aria-required="true"
          aria-describedby={errors.isin ? "isin-error" : "isin-hint"}
          aria-invalid={!!errors.isin}
          autoFocus
        />
        {errors.isin ? (
          <p id="isin-error" className="text-sm text-destructive" role="alert">
            {errors.isin.message}
          </p>
        ) : (
          <p id="isin-hint" className="text-xs text-muted-foreground">
            12-character alphanumeric code
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="issuer_id">Issuer *</Label>
        <CreatableSelect
          value={issuerId}
          onValueChange={(value) => setValue("issuerId", value)}
          options={issuerOptions}
          placeholder={
            isLoadingIssuers ? "Loading issuers..." : "Select or create issuer"
          }
          onCreate={handleCreateIssuer}
          disabled={isLoadingIssuers}
          aria-required
          aria-describedby={errors.issuerId ? "issuer-error" : undefined}
          aria-invalid={!!errors.issuerId}
        />
        {errors.issuerId && (
          <p
            id="issuer-error"
            className="text-sm text-destructive"
            role="alert">
            {errors.issuerId.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="coupon_rate">Coupon Rate (%) *</Label>
          <Input
            id="coupon_rate"
            type="number"
            step="0.01"
            min="0"
            max="100"
            {...register("couponRate", { valueAsNumber: true })}
            placeholder="e.g. 5.5"
            aria-required="true"
            aria-describedby={
              errors.couponRate ? "coupon-rate-error" : undefined
            }
            aria-invalid={!!errors.couponRate}
          />
          {errors.couponRate && (
            <p
              id="coupon-rate-error"
              className="text-sm text-destructive"
              role="alert">
              {errors.couponRate.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="tenor">Tenor *</Label>
          <Input
            id="tenor"
            {...register("tenor")}
            placeholder="e.g. 5 years"
            aria-required="true"
            aria-describedby={errors.tenor ? "tenor-error" : undefined}
            aria-invalid={!!errors.tenor}
          />
          {errors.tenor && (
            <p
              id="tenor-error"
              className="text-sm text-destructive"
              role="alert">
              {errors.tenor.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="nominal_price">Nominal Price *</Label>
          <Input
            id="nominal_price"
            type="number"
            step="0.01"
            min="0"
            {...register("nominalPrice", { valueAsNumber: true })}
            placeholder="e.g. 50000"
            aria-required="true"
            aria-describedby={
              errors.nominalPrice ? "nominal-price-error" : undefined
            }
            aria-invalid={!!errors.nominalPrice}
          />
          {errors.nominalPrice && (
            <p
              id="nominal-price-error"
              className="text-sm text-destructive"
              role="alert">
              {errors.nominalPrice.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="quantity">Quantity *</Label>
          <Input
            id="quantity"
            type="number"
            step="0.000001"
            min="0"
            {...register("quantity", { valueAsNumber: true })}
            placeholder="e.g. 10"
            aria-required="true"
            aria-describedby={errors.quantity ? "quantity-error" : undefined}
            aria-invalid={!!errors.quantity}
          />
          {errors.quantity && (
            <p
              id="quantity-error"
              className="text-sm text-destructive"
              role="alert">
              {errors.quantity.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="face_value">Face Value (Calculated)</Label>
        <Input
          id="face_value"
          type="text"
          value={faceValue.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          disabled
          className="bg-muted"
          aria-readonly="true"
          aria-describedby="face-value-hint"
        />
        <p id="face-value-hint" className="text-xs text-muted-foreground">
          Quantity × Nominal Price = {faceValue.toLocaleString()}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="maturity_date">Maturity Date *</Label>
          <Input
            id="maturity_date"
            type="date"
            {...register("maturityDate")}
            aria-required="true"
            aria-describedby={
              errors.maturityDate ? "maturity-date-error" : undefined
            }
            aria-invalid={!!errors.maturityDate}
          />
          {errors.maturityDate && (
            <p
              id="maturity-date-error"
              className="text-sm text-destructive"
              role="alert">
              {errors.maturityDate.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="currency">Currency *</Label>
          <Select
            value={watch("currency")}
            onValueChange={(value) => setValue("currency", value)}>
            <SelectTrigger
              id="currency"
              aria-required="true"
              aria-label="Select currency">
              <SelectValue />
            </SelectTrigger>
            {isLoadingCurrencies && "Loading Currencies"}
            {!isLoadingCurrencies && !errorLoadingCurrencies ? (
              <SelectContent>
                {availableCurrencies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            ) : (
              "Failed to load currencies"
            )}
          </Select>
          {errors.currency && (
            <p className="text-sm text-destructive" role="alert">
              {errors.currency.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="issue_date">Issue Date</Label>
        <Input
          id="issue_date"
          type="date"
          {...register("issueDate")}
          aria-describedby={errors.issueDate ? "issue-date-error" : undefined}
          aria-invalid={!!errors.issueDate}
        />
        {errors.issueDate && (
          <p
            id="issue-date-error"
            className="text-sm text-destructive"
            role="alert">
            {errors.issueDate.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="issue_number">Issue Number *</Label>
        <Input
          id="issue_number"
          {...register("issueNumber")}
          placeholder="e.g. IFB1/2024/5"
          aria-required="true"
          aria-describedby={
            errors.issueNumber ? "issue-number-error" : "issue-number-hint"
          }
          aria-invalid={!!errors.issueNumber}
        />
        {errors.issueNumber ? (
          <p
            id="issue-number-error"
            className="text-sm text-destructive"
            role="alert">
            {errors.issueNumber.message}
          </p>
        ) : (
          <p id="issue-number-hint" className="text-xs text-muted-foreground">
            Alphanumeric with special characters allowed
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Additional notes about this debt security"
          rows={3}
          aria-label="Additional notes"
        />
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          aria-label="Cancel">
          Cancel
        </Button>
        <Button
          type="submit"
          aria-label={
            debtSecurity ? "Update debt security" : "Create debt security"
          }>
          {debtSecurity ? "Update" : "Create"}
        </Button>
      </DialogFooter>
    </form>
  );
}
